import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, CalendarDays, MapPin, ExternalLink, Play, X } from "lucide-react";

const CATS = [
  { key: "", label: "Tutti" },
  { key: "Distretto", label: "Distretto" },
  { key: "Club", label: "Club" },
];

const formatDate = (d) => {
  if (!d) return "";
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const dt = new Date(d);
  if (!isNaN(dt)) return dt.toLocaleDateString("it-IT");
  return d;
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === "Escape") setActiveVideo(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (cat) params.category = cat;
    const { data } = await api.get("/events", { params });
    setEvents(data);
    setLoading(false);
  }, [q, cat]);

  useEffect(() => {
    const t = setTimeout(fetchEvents, 250);
    return () => clearTimeout(t);
  }, [fetchEvents]);

  return (
    <div className="space-y-8" data-testid="events-page">
      <div>
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Distretto & Club</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-2 tracking-tight">Eventi & News</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca eventi…" className="pl-10 h-11" data-testid="events-search" />
        </div>
        <div className="flex gap-2">
          {CATS.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`events-filter-${c.key || "all"}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${cat === c.key ? "bg-navy text-white" : "bg-white border border-navy/15 text-navy hover:bg-navy/5"}`}>
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {events.map((e) => (
          <article key={e.id} data-testid={`event-card-${e.id}`}
            className="bg-white rounded-lg border border-navy/10 overflow-hidden hover:shadow-md hover:-translate-y-1 transition-transform transition-shadow duration-200">
            <div className="aspect-video bg-navy/5 overflow-hidden flex items-center justify-center">
              {e.video_url ? (
                <button type="button" onClick={() => setActiveVideo(e.video_url)} data-testid={`event-video-${e.id}`}
                  className="group/vid relative w-full h-full block bg-black" title="Riproduci video">
                  <video src={`${e.video_url}#t=0.1`} muted playsInline preload="metadata" className="w-full h-full object-contain pointer-events-none" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover/vid:bg-black/35 transition-colors">
                    <span className="w-16 h-16 rounded-full bg-white/90 group-hover/vid:bg-white flex items-center justify-center shadow-lg group-hover/vid:scale-110 transition-transform duration-200">
                      <Play className="w-7 h-7 text-navy ml-1" fill="currentColor" />
                    </span>
                  </span>
                  <span className="absolute bottom-2 right-2 text-[10px] font-bold uppercase tracking-wider bg-black/60 text-white px-2 py-0.5 rounded">Video</span>
                </button>
              ) : e.image_url ? (
                <img src={e.image_url} alt={e.title} className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-navy/20"><CalendarDays className="w-10 h-10" /></div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-gold">
                <span className={`px-2 py-0.5 rounded ${e.category === "Distretto" ? "bg-navy text-white" : "bg-gold/15 text-navy-deep"}`}>{e.category}</span>
                {e.date && <span className="text-muted-foreground flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{e.end_date && e.end_date !== e.date ? `${formatDate(e.date)} – ${formatDate(e.end_date)}` : formatDate(e.date)}</span>}
              </div>
              <h3 className="font-display text-xl font-semibold text-navy mt-3 leading-snug">{e.title}</h3>
              {e.club && <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{e.club}</p>}
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-3">{e.description}</p>
              {e.link && (
                <a href={e.link} target="_blank" rel="noopener noreferrer" data-testid={`event-link-${e.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold transition-colors">
                  Leggi la news <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
      {!loading && events.length === 0 && (
        <div className="text-center py-16 text-muted-foreground" data-testid="events-empty">Nessun evento disponibile.</div>
      )}

      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setActiveVideo(null)} data-testid="video-modal">
          <button onClick={() => setActiveVideo(null)} data-testid="video-modal-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
            <X className="w-6 h-6" />
          </button>
          <video src={activeVideo} controls autoPlay playsInline onClick={(ev) => ev.stopPropagation()}
            className="max-w-[92vw] max-h-[88vh] rounded-lg shadow-2xl bg-black" data-testid="video-modal-player" />
        </div>
      )}
    </div>
  );
}
